import { useEffect, useState } from 'react';
import { authService } from '../services/api';
import { getAvatarUrl } from './utils/Common';

const DEPARTMENT_ORDER = [
  'Zarząd',
  'IT',
  'Controlling',
  'Dział Handlowy',
  'Technologia produkcji',
  'Logistyka',
  'Produkcja',
  'Magazyn',
  'Marketing',
  'Cybersecurity',
  'AI',
  'Testowy'
];

const ColleagueAvatar = ({ colleague, className }) => {
  const url = getAvatarUrl(colleague.avatar);
  if (url) {
    return <img src={url} alt={colleague.full_name} className={className} />;
  }
  return <span>{colleague.full_name?.charAt(0).toUpperCase() || '?'}</span>;
};

const Colleagues = () => {
  const [colleagues, setColleagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedDepartments, setExpandedDepartments] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('cards');

  useEffect(() => {
    loadColleagues();
  }, []);

  const loadColleagues = async () => {
    try {
      const data = await authService.getColleagues();
      setColleagues(data);
      const expanded = {};
      data.forEach(c => {
        expanded[c.department || 'Inne'] = true;
      });
      setExpandedDepartments(expanded);
    } catch (err) {
      setError('Nie udało się załadować listy współpracowników');
      console.error(err);
    }
    setLoading(false);
  };

  const toggleDepartment = (dept) => {
    setExpandedDepartments(prev => ({ ...prev, [dept]: !prev[dept] }));
  };

  const filteredColleagues = colleagues.filter(colleague => {
    const query = searchQuery.toLowerCase();
    return (
      colleague.full_name?.toLowerCase().includes(query) ||
      colleague.email?.toLowerCase().includes(query) ||
      colleague.department?.toLowerCase().includes(query) ||
      colleague.job_title?.toLowerCase().includes(query) ||
      colleague.phone_number?.toLowerCase().includes(query)
    );
  });

  const colleaguesByDepartment = filteredColleagues.reduce((acc, colleague) => {
    const dept = colleague.department || 'Inne';
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(colleague);
    return acc;
  }, {});

  Object.keys(colleaguesByDepartment).forEach(dept => {
    colleaguesByDepartment[dept].sort((a, b) =>
      (a.full_name || '').localeCompare(b.full_name || '', 'pl')
    );
  });

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h2>👥 Moi Współpracownicy</h2>
          <p>Ładowanie...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h2>👥 Moi Współpracownicy</h2>
          <p className="error-text">{error}</p>
        </div>
      </div>
    );
  }

  const sortedDepts = Object.entries(colleaguesByDepartment).sort(([a], [b]) => {
    const indexA = DEPARTMENT_ORDER.indexOf(a);
    const indexB = DEPARTMENT_ORDER.indexOf(b);
    
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return a.localeCompare(b, 'pl');
  });

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Moi współpracownicy</h2>
        <p>Przeglądaj zarejestrowanych pracowników firmy.</p>
      </div>

      <div className="page-content">
        {/* Toolbar: search + view toggle */}
        <div className="colleagues-toolbar">
          <div className="search-container colleagues-search">
            <input
              type="text"
              className="search-input"
              placeholder="🔍 Szukaj według imienia, e-maila, działu, stanowiska lub telefonu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>
          <div className="view-toggle">
            <button
              className={`view-toggle-btn${viewMode === 'cards' ? ' active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Widok kart"
            >
              ⊞ Karty
            </button>
            <button
              className={`view-toggle-btn${viewMode === 'list' ? ' active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Widok listy"
            >
              ≡ Lista
            </button>
          </div>
        </div>

        {filteredColleagues.length === 0 ? (
          <div className="no-results">
            <p>Brak wyników{searchQuery ? ` dla "${searchQuery}"` : ''}</p>
          </div>
        ) : (
          <div className="colleagues-grid">
            {sortedDepts.map(([department, deptColleagues]) => (
              <div key={department} className="department-section">
                <div
                  className="department-section-header"
                  onClick={() => toggleDepartment(department)}
                >
                  <h3>{department}</h3>
                  <span className="department-badge">
                    {deptColleagues.length}{' '}
                    {deptColleagues.length === 1 ? 'pracownik' : 'pracowników'}
                  </span>
                  <span className={`expand-arrow ${expandedDepartments[department] ? 'open' : ''}`}>▼</span>
                </div>

                {expandedDepartments[department] && (
                  viewMode === 'cards' ? (
                    /* ── CARD VIEW ── */
                    <div className="colleagues-row">
                      {deptColleagues.map(colleague => (
                        <div key={colleague.id} className="colleague-card-compact">
                          <div className="colleague-avatar-compact">
                            <ColleagueAvatar colleague={colleague} className="colleague-avatar-image" />
                          </div>
                          <div className="colleague-details-compact">
                            <h4 title={colleague.full_name}>{colleague.full_name}</h4>
                            <p className="colleague-position-compact" title={colleague.job_title || ''}>
                              {colleague.job_title || ''}
                            </p>
                            <hr className="colleague-card-divider" />
                            <p className="colleague-email-compact" title={colleague.email}>
                              <svg className="colleague-contact-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                              <span>{colleague.email}</span>
                            </p>
                            {colleague.phone_number && (
                              <p className="colleague-phone-compact" title={colleague.phone_number}>📞 {colleague.phone_number}</p>
                            )}
                            <span className={`status-badge-small ${colleague.is_active ? 'active' : 'inactive'}`}>
                              {colleague.is_active ? '● Aktywny' : '○ Nieaktywny'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* ── LIST VIEW ── */
                    <div className="colleagues-list-view">
                      <table className="colleagues-table">
                        <thead>
                          <tr>
                            <th></th>
                            <th>Imię i Nazwisko</th>
                            <th>Stanowisko</th>
                            <th>E-mail</th>
                            <th>Telefon</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deptColleagues.map(colleague => (
                            <tr key={colleague.id}>
                              <td>
                                <div className="col-list-avatar">
                                  <ColleagueAvatar colleague={colleague} className="col-list-avatar-img" />
                                </div>
                              </td>
                              <td className="col-list-name">{colleague.full_name}</td>
                              <td className="col-list-position">{colleague.job_title || '—'}</td>
                              <td className="col-list-email">{colleague.email}</td>
                              <td className="col-list-phone">{colleague.phone_number || '—'}</td>
                              <td>
                                <span className={`status-badge-small ${colleague.is_active ? 'active' : 'inactive'}`}>
                                  {colleague.is_active ? '● Aktywny' : '○ Nieaktywny'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Colleagues;
